import React, { useEffect, useRef } from 'react'
import { TextClamp } from '../../components/text/TextClamp'
import { SlideHeaderChrome } from '../../layouts/SlideHeaderChrome'
import { presetSlot } from '../../layouts/presetSlots'

import { EvidenceFlow } from './EvidenceFlow'
import { ExhibitPanel } from './ExhibitPanel'
import { exhibitContentOrigin, exhibitRect, exhibitSlot } from './layout'
import { MagazineFeature } from './MagazineFeature'
import { PairPanels } from './PairPanels'
import { TriptychPanels } from './TriptychPanels'
import type { ExhibitSource, FindingExhibitData } from './types'

/**
 * The callout footer's measured boxes (exhibit-callout `source` slot): the question
 * title on its own lines, then scope, unit, base and any group context as one run in
 * the scopeLabel box. Each box holds its fields' longest valid text, so neither clamps.
 */
const CALLOUT_FOOTER = presetSlot('metric-callout', 'source').parts!
const CALLOUT_QUESTION_BOX = CALLOUT_FOOTER.sourceLabel
const CALLOUT_ESSENTIALS_BOX = CALLOUT_FOOTER.scopeLabel
const CALLOUT_FOOTER_PAD_BOTTOM = 12

function calloutEssentials(source: ExhibitSource): string {
  return [source.scopeLabel, source.unitLabel, source.baseLabel].filter(Boolean).join(' · ')
}

/** A complete headline remains visible; the fixture flags measured overflow for review. */
export function measureHeadlineFit(element: HTMLElement): 'fit' | 'overflow' {
  // Natural text is judged against the generated layout budget. clientHeight
  // stays in layout pixels when DeckViewer scales the canvas; font ink may
  // extend scrollHeight beyond an otherwise valid two-line block.
  if (element.dataset.fitBudget) {
    return element.clientHeight > Number(element.dataset.fitBudget) ? 'overflow' : 'fit'
  }
  return element.scrollHeight > element.clientHeight ? 'overflow' : 'fit'
}

/** Shared exhibit renderer. The caller binds and validates evidence before rendering. */
export function FindingExhibitSlide({ data }: { data: FindingExhibitData }) {
  const type = data.type
  const evidence = type.startsWith('evidence-')
  const headlineRect = exhibitRect(type, 'headline')
  const originY = evidence ? exhibitContentOrigin(type) : 0
  const supportingRect = evidence ? exhibitRect(type, 'supportingText') : null
  const titleGap = supportingRect ? supportingRect.y - headlineRect.y - headlineRect.h : 0
  const panelGap = supportingRect ? originY - supportingRect.y - supportingRect.h : 0
  const slot = (name: string) => exhibitSlot(type, name, originY)
  const articleRef = useRef<HTMLElement>(null)
  const headlineRef = useRef<HTMLHeadingElement>(null)
  const regionRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const headline = headlineRef.current
    const article = articleRef.current
    if (!headline || !article) return
    const measured = [headline, ...article.querySelectorAll<HTMLElement>('[data-fit-field]')]
    let active = true
    const update = () => {
      if (!active) return
      for (const element of measured) {
        const field = element.dataset.fitField ?? 'headline'
        element.dataset.fitWarning = measureHeadlineFit(element) === 'overflow' ? `${field}-overflow` : ''
      }
    }
    update()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update)
    measured.forEach((element) => observer?.observe(element))
    const budgets = typeof MutationObserver === 'undefined' ? null : new MutationObserver(update)
    measured.forEach((element) => budgets?.observe(element, { attributes: true, attributeFilter: ['data-fit-budget'] }))
    void document.fonts?.ready.then(update)
    return () => { active = false; observer?.disconnect(); budgets?.disconnect() }
  }, [data])
  return (
    <article ref={articleRef} data-testid="finding-exhibit" data-exhibit-type={type} style={{ position: 'relative', width: 1400, height: 787, boxSizing: 'border-box', background: 'var(--slide-bg, #fff)', color: 'var(--slide-text-primary, #192c27)', overflow: 'visible', fontFamily: 'Inter, sans-serif' }}>
      <header style={{ ...exhibitSlot(type, 'header'), left: 0, width: '100%' }}>
        <SlideHeaderChrome eyebrowText={data.eyebrow} clientLogoUrl={data.clientLogoUrl} eyebrowTextTransform="none" />
      </header>
      <div style={evidence ? { position: 'absolute', left: 0, top: headlineRect.y, width: 1400, height: 787 - headlineRect.y - 40, display: 'flex', flexDirection: 'column' } : { display: 'contents' }}>
      <div style={evidence ? { marginLeft: headlineRect.x, width: headlineRect.w, flexShrink: 0 } : { display: 'contents' }}>
      <h1 data-fit-budget={evidence ? headlineRect.h : undefined} ref={headlineRef} data-testid="exhibit-headline" style={{ ...(evidence ? { width: headlineRect.w } : exhibitSlot(type, 'headline')), margin: 0, fontFamily: 'Sumana, Georgia, serif', fontSize: type === 'metric-callout' ? 50 : 40, fontWeight: 400, lineHeight: 1.25, color: 'var(--slide-text-primary, #172c25)', overflowWrap: 'anywhere', overflow: 'visible', textAlign: type === 'metric-callout' ? 'center' : 'left', textWrap: type === 'metric-callout' ? 'balance' : undefined }}>{data.actionTitle}</h1>

      {'supportingText' in data && data.supportingText && type.startsWith('evidence-') ? <p data-testid="exhibit-supporting-text" data-fit-field="supporting-text" data-fit-budget={supportingRect?.h} style={{ width: supportingRect?.w, margin: `${titleGap}px 0 0`, fontSize: 20, lineHeight: 1.35, overflowWrap: 'anywhere', overflow: 'visible' }}>{data.supportingText}</p> : null}

      </div>
      <EvidenceFlow.Provider value={evidence ? regionRef : null}>
      <div ref={regionRef} data-fit-field={evidence ? 'evidence-region' : undefined} style={evidence ? { position: 'relative', flex: '1 1 auto', minHeight: 0, marginTop: panelGap } : { display: 'contents' }}>
      {type === 'evidence-single' ? <div style={slot('main')}><ExhibitPanel panel={data.main} testId="exhibit-main" locale={data.locale} /></div> : null}

      {type === 'evidence-rail' ? <>
        <div style={slot('main')}><ExhibitPanel panel={data.main} testId="exhibit-main" locale={data.locale} /></div>
        {data.support.kind === 'interpretation' ? (
          <aside data-testid="exhibit-support" style={{ ...slot('support'), boxSizing: 'border-box', padding: 24, fontFamily: 'Sumana, Georgia, serif', fontSize: 30, lineHeight: 1.25, color: 'var(--slide-text-primary, #173d30)', overflowWrap: 'anywhere' }}>{data.support.text}</aside>
        ) : data.support.composition === 'single' ? (
          <aside data-testid="exhibit-support" style={{ ...slot('support'), boxSizing: 'border-box' }}><ExhibitPanel panel={data.support.panel} testId="exhibit-support-panel" locale={data.locale} /></aside>
        ) : <>
          <aside data-testid="exhibit-support-upper" style={{ ...slot('supportUpper'), boxSizing: 'border-box' }}><ExhibitPanel panel={data.support.metric} testId="exhibit-support-metric" presentation="rail-metric" locale={data.locale} /></aside>
          <aside data-testid="exhibit-support-lower" style={{ ...slot('supportLower'), boxSizing: 'border-box' }}><ExhibitPanel panel={data.support.quote} testId="exhibit-support-quote" presentation="rail-quote" locale={data.locale} /></aside>
        </>}
      </> : null}

      {type === 'evidence-pair' ? <PairPanels data={data} originY={originY} /> : null}

      {type === 'evidence-multiples' ? <TriptychPanels data={data} originY={originY} /> : null}

      {type === 'respondent-feature' ? <MagazineFeature data={data} /> : null}

      {type === 'metric-callout' ? <>
        <div style={{ ...exhibitSlot(type, 'value'), display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
          <div data-testid="exhibit-callout-value" style={{ fontFamily: 'Sumana, Georgia, serif', fontSize: 150, color: 'var(--slide-accent)', letterSpacing: '-0.06em', lineHeight: 1 }}>{data.metric.displayValue}</div>
          <div data-testid="exhibit-callout-label" data-fit-field="callout-label" style={{ marginTop: 20, maxWidth: '100%', fontSize: 24, lineHeight: 1.2, color: 'var(--slide-text-secondary, #4f6558)', textWrap: 'balance' }}>{data.metric.label}</div>
        </div>
        <footer data-testid="exhibit-callout-provenance" data-source-id={data.metric.source.sourceId} data-source-label={data.metric.source.sourceLabel} style={{ ...exhibitSlot(type, 'source'), display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', minWidth: 0, boxSizing: 'border-box', paddingBottom: CALLOUT_FOOTER_PAD_BOTTOM, color: 'var(--ds-text-faint, var(--slide-text-muted, #607067))', fontSize: 'var(--ds-fs-footer, 11px)' }}>
          <div style={{ borderTop: 'var(--ds-border-thin) solid var(--ds-border-color)', textAlign: 'right' }}>
            <TextClamp minLines={1} maxLines={CALLOUT_QUESTION_BOX.maxLines} lineHeight={CALLOUT_QUESTION_BOX.lineHeight} data-testid="exhibit-callout-question">{data.metric.source.sourceLabel}</TextClamp>
            {calloutEssentials(data.metric.source) ? <TextClamp minLines={1} maxLines={CALLOUT_ESSENTIALS_BOX.maxLines} lineHeight={CALLOUT_ESSENTIALS_BOX.lineHeight} data-testid="exhibit-callout-essentials">{calloutEssentials(data.metric.source)}</TextClamp> : null}
          </div>
        </footer>
      </> : null}
      </div>
      </EvidenceFlow.Provider>
      </div>
    </article>
  )
}
